import { useState, useEffect, useCallback, useRef } from 'react'
import { useBestaetigung } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, FilterZurueck, KennzahlenLeiste } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge } from '../components/StatusBadge'
import { PageHeader, Card, Button, Input, Select } from '@pilot-hub/ui'
import { api, licenseApi, userApi } from '@pilot-hub/api-client'
import { ArrowLeft, Plus, Edit, Eye, Trash2, FileText, Upload, ExternalLink, Loader2, Save } from 'lucide-react'
import { DatumFeld } from '../components/DatumFelder'

interface Lizenz {
  id: number
  nutzer_id: number
  typ: string
  ausstellungsdatum: string | null
  ablaufdatum: string | null
  ausstellende_behoerde: string | null
  dokument_pfad: string | null
  lizenznummer: string | null
}

interface LizenzWithDoc extends Lizenz {
  hasDocument: boolean
}

interface Nutzer { id: number; benutzername: string; vorname?: string; nachname?: string }

const LEER = {
  nutzer_id: '',
  typ: '',
  ausstellungsdatum: '',
  ablaufdatum: '',
  ausstellende_behoerde: ''
}

function statusVon(ablauf: string | null): { label: string; variant: 'success' | 'warning' | 'danger' | 'info' } {
  if (!ablauf) return { label: 'Ohne Ablauf', variant: 'info' }
  const heute = new Date()
  const d = new Date(ablauf)
  if (d < heute) return { label: 'Abgelaufen', variant: 'danger' }
  const in6Mon = new Date(heute.getFullYear(), heute.getMonth() + 6, heute.getDate())
  if (d <= in6Mon) return { label: 'Läuft ab', variant: 'warning' }
  return { label: 'Gültig', variant: 'success' }
}

const TON: Record<string, 'ok' | 'warn' | 'bad' | 'neutral' | 'info'> = { success: 'ok', warning: 'warn', danger: 'bad', info: 'neutral' }

const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; lizenzId?: number }

export function LicensesPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const [lizenzen, setLizenzen] = useState<Lizenz[]>([])
  const [nutzer, setNutzer] = useState<Nutzer[]>([])
  const [typen, setTypen] = useState<string[]>([])
  const [search, setSearch] = usePersistState('lizenzen.search', '')
  const [typFilter, setTypFilter] = usePersistState('lizenzen.typFilter', 'all')
  const [personFilter, setPersonFilter] = usePersistState('lizenzen.personFilter', 'all')
  const [gueltigFilter, setGueltigFilter] = usePersistState('lizenzen.gueltigFilter', 'all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [speichert, setSpeichert] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileInputRefDetail = useRef<HTMLInputElement>(null)
  const [fileInputKey, setFileInputKey] = useState(0)
  /** Beim Anlegen gewähltes PDF – wird nach dem Speichern der Lizenz hochgeladen */
  const [neueDatei, setNeueDatei] = useState<File | null>(null)

  // PDF-Anzeige: iFrame und neuer Tab senden keinen Authorization-Header (auch iPad/Safari) –
  // daher holt die Seite einen kurzlebigen, signierten Link für genau dieses Dokument.
  const [dokUrl, setDokUrl] = useState<string | null>(null)
  const [dokFehler, setDokFehler] = useState<string | null>(null)
  /** Erhöht nach jedem Upload, damit der neue Inhalt geladen wird */
  const [dokStand, setDokStand] = useState(0)
  const dokLizenz = view.lizenzId ? lizenzen.find(l => l.id === view.lizenzId) : null
  useEffect(() => {
    setDokUrl(null); setDokFehler(null)
    if (!dokLizenz?.id || !dokLizenz.dokument_pfad) return
    let aktiv = true
    api.get(`/lizenzen/${dokLizenz.id}/dokument/link`)
      .then(r => { if (aktiv) setDokUrl(`${(r.data as any).url}&v=${dokStand}`) })
      .catch((e: any) => { if (aktiv) setDokFehler(e?.response?.data?.detail || 'Dokument konnte nicht geladen werden') })
    return () => { aktiv = false }
  }, [dokLizenz?.id, dokLizenz?.dokument_pfad, dokStand])

  /** Aus der Liste: Tab sofort (im Klick) öffnen, dann den Link setzen – sonst blockiert Safari das Popup. */
  async function dokumentImNeuenTab(lizenzId: number) {
    const tab = window.open('', '_blank')
    try {
      const r = await api.get(`/lizenzen/${lizenzId}/dokument/link`)
      if (tab) tab.location.href = (r.data as any).url
      else window.location.href = (r.data as any).url
    } catch (e: any) {
      tab?.close()
      setFehler(e?.response?.data?.detail || 'Dokument konnte nicht geöffnet werden')
    }
  }
  
  const triggerFileInput = (ref: React.RefObject<HTMLInputElement>) => {
    ref.current?.click()
  }

  const geaendert = useUngespeichert(form, JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [l, n, t] = await Promise.all([
        licenseApi.list({ size: 500 }),
        userApi.list({ size: 500 }),
        licenseApi.getTypes()
      ])
      setLizenzen((l.data as any).items ?? (l.data as any) ?? [])
      const alleNutzer = (n.data as any).items ?? (n.data as any) ?? []
      setNutzer(alleNutzer)
      setTypen(Array.isArray(t.data) ? (t.data as string[]) : [])
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Lizenzen konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])

  const nutzerName = (id: number) => {
    const u = nutzer.find(x => x.id === id)
    return u ? (`${u.vorname ?? ''} ${u.nachname ?? ''}`.trim() || u.benutzername) : `#${id}`
  }

  function oeffneNeu() {
    setForm({ ...LEER, typ: typen[0] || '' })
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(l: Lizenz) {
    setForm({
      nutzer_id: String(l.nutzer_id ?? ''),
      typ: l.typ ?? '',
      ausstellungsdatum: l.ausstellungsdatum ? l.ausstellungsdatum.slice(0, 10) : '',
      ablaufdatum: l.ablaufdatum ? l.ablaufdatum.slice(0, 10) : '',
      ausstellende_behoerde: l.ausstellende_behoerde ?? ''
    })
    setView({ mode: 'edit', lizenzId: l.id })
  }

  function oeffneDetail(l: Lizenz) {
    setView({ mode: 'detail', lizenzId: l.id })
    setForm({
      nutzer_id: String(l.nutzer_id ?? ''),
      typ: l.typ,
      ausstellungsdatum: l.ausstellungsdatum ? l.ausstellungsdatum.slice(0, 10) : '',
      ablaufdatum: l.ablaufdatum ? l.ablaufdatum.slice(0, 10) : '',
      ausstellende_behoerde: l.ausstellende_behoerde || '',
      lizenznummer: l.lizenznummer || '',
    })
  }

  function zurueckZurListe() {
    setNeueDatei(null)
    setView({ mode: 'list' })
    setForm({ ...LEER, typ: typen[0] || '' })
  }

  async function speichern() {
    if (!form.nutzer_id || !form.typ) return
    setSpeichert(true)
    try {
      const payload: any = {
        nutzer_id: Number(form.nutzer_id),
        typ: form.typ,
        ausstellungsdatum: form.ausstellungsdatum || null,
        ablaufdatum: form.ablaufdatum || null,
        ausstellende_behoerde: form.ausstellende_behoerde || null
      }
      if (view.mode === 'edit' && view.lizenzId) await licenseApi.update(view.lizenzId, payload)
      else {
        const neu = await licenseApi.create(payload)
        const neueId = (neu.data as any)?.id
        if (neueDatei && neueId) await ladeHoch(neueDatei, neueId)
      }
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await licenseApi.delete(id)
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen')
    }
  }

  // Chunked upload for large files (bypass Cloudflare 1MB limit)
  const CHUNK_SIZE = 512 * 1024 // 512KB chunks (under Cloudflare 1MB limit)

  async function uploadDocumentChunked(file: File, token: string, lizenzId: number) {
    
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
    console.log('[uploadDocumentChunked] Total chunks:', totalChunks)
    
    // 1. Initialize upload
    const initResponse = await fetch(`/api/v1/lizenzen/${lizenzId}/dokument/init`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ filename: file.name, total_chunks: totalChunks })
    })
    
    if (!initResponse.ok) {
      const error = await initResponse.json()
      throw new Error(error.detail || 'Initialisierung fehlgeschlagen')
    }
    
    const { upload_id } = await initResponse.json()
    
    // 2. Upload chunks sequentially
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, file.size)
      const chunk = file.slice(start, end)
      
      const chunkFormData = new FormData()
      chunkFormData.append('upload_id', upload_id)
      chunkFormData.append('chunk_index', String(i))
      chunkFormData.append('file', chunk, `${file.name}.part${i}`)
      
      const chunkResponse = await fetch(`/api/v1/lizenzen/${lizenzId}/dokument/chunk`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: chunkFormData
      })
      
      if (!chunkResponse.ok) {
        const error = await chunkResponse.json()
        throw new Error(`Chunk ${i + 1}/${totalChunks} fehlgeschlagen: ${error.detail || 'Unbekannter Fehler'}`)
      }
      
      // Progress feedback
      setFehler(`Lade Chunk ${i + 1} von ${totalChunks}...`)
    }
    
    // 3. Complete upload
    const completeResponse = await fetch(`/api/v1/lizenzen/${lizenzId}/dokument/complete`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ upload_id })
    })
    
    if (!completeResponse.ok) {
      const error = await completeResponse.json()
      throw new Error(error.detail || 'Abschluss fehlgeschlagen')
    }
  }

  async function uploadDirect(file: File, token: string, lizenzId: number) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`/api/v1/lizenzen/${lizenzId}/dokument`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    
    const contentType = response.headers.get('content-type')
    let errorDetail = `Upload fehlgeschlagen (${response.status})`
    
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json()
      errorDetail = errorData.detail || errorDetail
    } else {
      const text = await response.text()
      if (text) errorDetail = text.substring(0, 200)
    }
    
    if (!response.ok) {
      throw new Error(errorDetail)
    }
  }


  async function uploadDocument(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Eingabe zurücksetzen, damit dieselbe Datei erneut gewählt werden kann
    setFileInputKey(prev => prev + 1)
    if (!file) return
    // iOS Safari sometimes doesn't set the correct MIME type
    // Check both MIME type and file extension
    const isPdf = file.type === 'application/pdf' || 
                  file.name.toLowerCase().endsWith('.pdf')
    if (!isPdf) {
      setFehler('Nur PDF-Dateien erlaubt (Datei muss .pdf Endung haben)')
      return
    }
    // Neue Lizenz: Datei merken und nach dem Speichern hochladen
    if (!currentLizenz) {
      setFehler(null)
      setNeueDatei(file)
      return
    }
    await ladeHoch(file, currentLizenz.id)
  }

  async function ladeHoch(file: File, lizenzId: number) {
    
    // Extract token same way as api-client
    const getToken = () => {
      const authKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
      const authData = localStorage.getItem(authKey)
      if (authData) {
        try {
          const parsed = JSON.parse(authData)
          return parsed.accessToken || parsed.access_token || parsed.state?.accessToken || parsed.state?.access_token
        } catch {}
      }
      return localStorage.getItem('access_token')
    }
    
    const token = getToken()
    if (!token) {
      setFehler('Kein Zugriffstoken gefunden. Bitte neu anmelden.')
      return
    }
    
    setUploading(true)
    setFehler(null)
    try {
      // Use chunked upload for files > 500KB to bypass Cloudflare 1MB limit
      if (file.size > 500 * 1024) {
        await uploadDocumentChunked(file, token, lizenzId)
      } else {
        await uploadDirect(file, token, lizenzId)
      }
      setShowUpload(false)
      await laden()
      setDokStand(n => n + 1)
    } catch (e: any) {
      setFehler(e?.message || 'Upload fehlgeschlagen')
    } finally {
      setUploading(false)
    }
  }

  const gefiltert = lizenzen.filter(l =>
    (typFilter === 'all' || l.typ === typFilter) &&
    (personFilter === 'all' || String(l.nutzer_id) === personFilter) &&
    (gueltigFilter === 'all' || statusVon(l.ablaufdatum).label === gueltigFilter) && (
    nutzerName(l.nutzer_id).toLowerCase().includes(search.toLowerCase()) ||
    (l.typ ?? '').toLowerCase().includes(search.toLowerCase()))
  )
  const personenMitLizenz = nutzer.filter(u => lizenzen.some(l => l.nutzer_id === u.id))

  const columns: SpalteDef[] = [
    { key: 'nutzer', header: 'Nutzer', accessor: 'nutzer_id', sortWert: (l: any) => nutzerName(l.nutzer_id), render: (_v: any, l: any) => nutzerName(l.nutzer_id) },
    { key: 'typ', header: 'Lizenztyp', accessor: 'typ' },
    { key: 'behoerde', header: 'Behörde', accessor: 'ausstellende_behoerde', mobile: 'hidden', render: (_v: any, l: any) => l.ausstellende_behoerde || '—' },
    { key: 'ausstellung', header: 'Ausgestellt', accessor: 'ausstellungsdatum', mobile: 'hidden', render: (_v: any, l: any) => fmt(l.ausstellungsdatum) },
    { key: 'ablauf', header: 'Ablauf', accessor: 'ablaufdatum', render: (_v: any, l: any) => fmt(l.ablaufdatum) },
    { key: 'status', header: 'Status', accessor: 'id', sortWert: (l: any) => statusVon(l.ablaufdatum).label, render: (_v: any, l: any) => { const s = statusVon(l.ablaufdatum); return <StatusBadge label={s.label} ton={TON[s.variant]} /> } },
    { 
      key: 'dokument', header: 'Dokument', accessor: 'dokument_pfad', mobile: 'actions',
      render: (_v: any, l: any) => {
        const hasDoc = l.dokument_pfad && l.dokument_pfad !== '';
        return (
          <ZeilenAktionen name={`Lizenz ${l.typ || ''}`.trim()} eintraege={hasDoc ? [{ label: 'PDF anzeigen', icon: <ExternalLink className="h-4 w-4" />, onClick: () => dokumentImNeuenTab(l.id) }] : undefined} onBearbeiten={() => oeffneBearbeiten(l)} onLoeschen={async () => { const name = `${l.typ || 'Lizenz'} (${nutzerName(l.nutzer_id)})`; if (await bestaetigen({ titel: 'Lizenz löschen?', text: 'Lizenz', objekt: String(name), hinweis: 'Das kann nicht rückgängig gemacht werden.' })) licenseApi.delete(l.id).then(laden) }} />
        )
      }
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          title=""
          subtitle="Fernpiloten- und sonstige Nachweise der Nutzer verwalten"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neue Lizenz</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
            <Input placeholder="Nutzer oder Typ" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-72" />
            <FilterZurueck aktiv={!!search || typFilter !== 'all' || personFilter !== 'all' || gueltigFilter !== 'all'} onClick={() => { setSearch(''); setTypFilter('all'); setPersonFilter('all'); setGueltigFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {lizenzen.length}</div>
          </div>
          <div className="mb-4"><KennzahlenLeiste items={[
            { label: 'Alle Lizenzen', anzahl: lizenzen.length, aktiv: gueltigFilter === 'all', onClick: () => setGueltigFilter('all') },
            ...(['Gültig', 'Läuft ab', 'Abgelaufen', 'Ohne Ablauf'] as const).map(g => ({
              label: g, anzahl: lizenzen.filter(l => statusVon(l.ablaufdatum).label === g).length, aktiv: gueltigFilter === g,
              punkt: g === 'Gültig' ? 'bg-emerald-400' : g === 'Läuft ab' ? 'bg-amber-400' : g === 'Abgelaufen' ? 'bg-red-400' : 'bg-slate-400',
              onClick: () => setGueltigFilter(gueltigFilter === g ? 'all' : g),
            })),
          ]} /></div>
          <div className="mb-4 grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select value={typFilter} onChange={e => setTypFilter(e.target.value)} placeholder="Lizenzart"
              options={[{ value: 'all', label: 'Alle Lizenzarten' }, ...typen.map(t => ({ value: t, label: t }))]} />
            <Select value={personFilter} onChange={e => setPersonFilter(e.target.value)} placeholder="Person"
              options={[{ value: 'all', label: 'Alle Personen' }, ...personenMitLizenz.map(u => ({ value: String(u.id), label: nutzerName(u.id) }))]} />
            <Select value={gueltigFilter} onChange={e => setGueltigFilter(e.target.value)} placeholder="Gültigkeit"
              options={[{ value: 'all', label: 'Alle Gültigkeiten' }, { value: 'Gültig', label: 'Gültig' }, { value: 'Läuft ab', label: 'Läuft ab (6 Monate)' }, { value: 'Abgelaufen', label: 'Abgelaufen' }, { value: 'Ohne Ablauf', label: 'Ohne Ablauf' }]} />
          </div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Lizenzen…</div> : (
            <ListenTabelle persistKey="lizenzen" data={gefiltert} columns={columns} keyAccessor={(l: any) => String(l.id)} onRowClick={oeffneDetail} emptyMessage="Keine Lizenzen vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW ======
  const currentLizenz = view.lizenzId ? lizenzen.find(l => l.id === view.lizenzId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreate ? 'Neue Lizenz' : isEdit ? 'Lizenz bearbeiten' : 'Lizenz anzeigen'}
        subtitle={isCreate ? 'Neue Lizenz anlegen' : currentLizenz ? nutzerName(currentLizenz.nutzer_id) : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentLizenz && <Button onClick={() => oeffneBearbeiten(currentLizenz)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        }
      />

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

      <Card>
        <div className="space-y-4">
          <Select 
            label="Nutzer" 
            id="lizenz-nutzer" 
            name="nutzer_id" 
            value={form.nutzer_id} 
            onChange={e => setForm({...form, nutzer_id: e.target.value})} 
            disabled={isDetail}
            options={nutzer.map(u => ({ value: String(u.id), label: nutzerName(u.id) }))} 
            placeholder="— Nutzer wählen —" 
          />
          <Select 
            label="Lizenztyp" 
            id="lizenz-typ" 
            name="typ" 
            value={form.typ} 
            onChange={e => setForm({...form, typ: e.target.value})} 
            disabled={isDetail}
            options={[...typen, ...(form.typ && !typen.includes(form.typ) ? [form.typ] : [])].map(t => ({ value: t, label: t }))} 
            placeholder="— Typ wählen —" 
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="lizenz-ausstellungsdatum" className="label">Ausstellungsdatum</label>
              <DatumFeld id="lizenz-ausstellungsdatum" name="ausstellungsdatum"  value={form.ausstellungsdatum} onChange={v => setForm({...form, ausstellungsdatum: v})} disabled={isDetail} />
            </div>
            <div>
              <label htmlFor="lizenz-ablaufdatum" className="label">Ablaufdatum</label>
              <DatumFeld id="lizenz-ablaufdatum" name="ablaufdatum"  value={form.ablaufdatum} onChange={v => setForm({...form, ablaufdatum: v})} disabled={isDetail} />
            </div>
          </div>
          <div>
            <label htmlFor="lizenz-behoerde" className="label">Ausstellende Behörde</label>
            <Input id="lizenz-behoerde" name="ausstellende_behoerde" value={form.ausstellende_behoerde} onChange={e => setForm({...form, ausstellende_behoerde: e.target.value})} disabled={isDetail} placeholder="z.B. Luftfahrt-Bundesamt" />
          </div>

          {/* PDF Upload für Create/Edit */}
          {!isDetail && (
            <div className="pt-4 border-t border-dark-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-dark-text flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary-400" /> Lizenz-Dokument (PDF)
                </h4>
              </div>
              {currentLizenz?.dokument_pfad ? (
                <>
                  <div className="mb-3">
                    {dokUrl ? (
                      <iframe src={dokUrl} className="w-full h-[300px] rounded-lg border border-dark-border bg-white" title="Lizenz-Dokument" />
                    ) : (
                      <div className="flex h-[120px] items-center justify-center rounded-lg border border-dark-border text-sm text-dark-text-muted">
                        {dokFehler || 'Lade Dokument …'}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-full max-w-xs">
                      <button 
                        type="button"
                        onClick={() => triggerFileInput(fileInputRef)}
                        className="flex items-center gap-2 w-full px-4 py-2 rounded-lg border border-primary-500/60 bg-primary-600/15 text-primary-100 font-medium hover:bg-primary-600/25 transition-colors cursor-pointer"
                      >
                        <Upload className="h-4 w-4" />
                        PDF ersetzen
                      </button>
                      <input 
                        ref={fileInputRef}
                        key={fileInputKey}
                        type="file" 
                        accept=".pdf" 
                        onChange={uploadDocument} 
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-6 text-center border-2 border-dashed border-dark-border rounded-lg bg-dark-bg-tertiary/50">
                  <FileText className="mx-auto mb-2 h-10 w-10 text-dark-text-muted" />
                  <p className="text-dark-text-muted mb-4">
                    {neueDatei ? <>Ausgewählt: <span className="text-dark-text">{neueDatei.name}</span> – wird beim Speichern hochgeladen</> : 'Kein PDF-Dokument hochgeladen'}
                  </p>
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-full max-w-xs">
                      <button 
                        type="button"
                        onClick={() => triggerFileInput(fileInputRef)}
                        className="flex items-center gap-2 w-full px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors cursor-pointer"
                      >
                        <Upload className="h-4 w-4" />
                        PDF hochladen
                      </button>
                      <input 
                        ref={fileInputRef}
                        key={fileInputKey}
                        type="file" 
                        accept=".pdf" 
                        onChange={uploadDocument} 
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {isDetail && currentLizenz && (
            <>
              <div className="pt-4 border-t border-dark-border">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-dark-text-muted">Status:</span> <StatusBadge label={statusVon(currentLizenz.ablaufdatum).label} ton={TON[statusVon(currentLizenz.ablaufdatum).variant]} /></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Ausgestellt am:</span> <span>{fmt(currentLizenz.ausstellungsdatum)}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Ablaufdatum:</span> <span>{fmt(currentLizenz.ablaufdatum)}</span></div>
                  <div className="flex justify-between"><span className="text-dark-text-muted">Ausstellende Behörde:</span> <span>{currentLizenz.ausstellende_behoerde || '—'}</span></div>
                </dl>
              </div>
              
              <div className="pt-4 border-t border-dark-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-dark-text flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary-400" /> Lizenz-Dokument (PDF)
                  </h4>
                  {currentLizenz.dokument_pfad && (
                    <a
                      href={dokUrl ?? undefined}
                      target="_blank"
                      rel="noopener"
                      aria-disabled={!dokUrl}
                      className={`flex items-center gap-1 text-sm transition-colors ${dokUrl ? 'text-primary-400 hover:text-primary-300' : 'pointer-events-none text-dark-text-subtle'}`}
                      title="PDF in neuem Tab öffnen"
                    >
                      <ExternalLink className="h-4 w-4" />
                      PDF öffnen
                    </a>
                  )}
                </div>
                {currentLizenz.dokument_pfad ? (
                  <>
                    {dokUrl ? (
                      <iframe src={dokUrl} className="w-full h-[500px] rounded-lg border border-dark-border bg-white" title="Lizenz-Dokument" />
                    ) : (
                      <div className="flex h-[120px] items-center justify-center rounded-lg border border-dark-border text-sm text-dark-text-muted">
                        {dokFehler || 'Lade Dokument …'}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-dark-text-subtle">Auf iPad/iPhone zeigt die Vorschau nur die erste Seite – „PDF öffnen“ zeigt das ganze Dokument.</p>
                    {!isDetail && (
                      <div className="mt-3 flex justify-end">
                        <div className="relative">
                          <button 
                            type="button"
                            onClick={() => triggerFileInput(fileInputRefDetail)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-500/60 bg-primary-600/15 text-primary-100 font-medium hover:bg-primary-600/25 transition-colors cursor-pointer"
                          >
                            <Upload className="h-4 w-4" />
                            PDF ersetzen
                          </button>
                          <input 
                            ref={fileInputRefDetail}
                            key={fileInputKey}
                            type="file" 
                            accept=".pdf" 
                            onChange={uploadDocument} 
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-6 text-center border-2 border-dashed border-dark-border rounded-lg bg-dark-bg-tertiary/50">
                    <FileText className="mx-auto mb-2 h-10 w-10 text-dark-text-muted" />
                    <p className="text-dark-text-muted mb-4">
                    {neueDatei ? <>Ausgewählt: <span className="text-dark-text">{neueDatei.name}</span> – wird beim Speichern hochgeladen</> : 'Kein PDF-Dokument hochgeladen'}
                  </p>
                    {!isDetail && (
                      <div className="relative w-full max-w-xs mx-auto">
                        <button 
                          type="button"
                          onClick={() => triggerFileInput(fileInputRefDetail)}
                          className="flex items-center gap-2 w-full px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors cursor-pointer"
                        >
                          <Upload className="h-4 w-4" />
                          PDF hochladen
                        </button>
                        <input 
                          ref={fileInputRefDetail}
                          key={fileInputKey}
                          type="file" 
                          accept=".pdf" 
                          onChange={uploadDocument} 
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={!form.nutzer_id || !form.typ} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}